import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { ModalDropdownOptionGroup } from "./ModalDropdownField";

const inputStyle: React.CSSProperties = {
  padding: "0.75rem",
  paddingRight: "2rem",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius)",
  backgroundColor: "var(--color-surface)",
  color: "var(--color-text)",
  fontSize: "var(--font-body)",
  width: "100%",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  marginBottom: "0.5rem",
  fontWeight: 600,
  display: "block",
};

const PANEL_GAP = 4;

export interface ComboSelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  optionGroups: ModalDropdownOptionGroup[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  wrapperStyle?: React.CSSProperties;
  /** When true and label is empty, hide the label to save space */
  compact?: boolean;
  /**
   * Vrije invoer: de getypte tekst ís de waarde (elke toetsaanslag →
   * onChange); de opties zijn dan suggesties. Zonder deze vlag is typen
   * alleen filteren en valt het veld bij sluiten terug op het gekozen label.
   */
  allowFreeText?: boolean;
  /** Tekst als filteren niets oplevert (standaard: categorieën). */
  geenOptiesTekst?: string;
}

interface FlatOption {
  value: string;
  label: string;
  toelichting?: string;
  group: string;
}

/**
 * Doorzoekbare dropdown: een tekstveld dat bij focus/klik een compact
 * portal-panel met alle (gegroepeerde) opties toont; typen filtert de lijst.
 * Vervangt de native <select> waar de optielijst te lang is om te scannen
 * (documentcategorieën) of waar kiezen én vrij typen samen moeten kunnen
 * (ontvanger-adres). Positionering/sluitgedrag volgt ActionMenu.
 */
export function ComboSelectField({
  label,
  value,
  onChange,
  optionGroups,
  placeholder,
  required,
  disabled,
  id,
  wrapperStyle,
  compact,
  allowFreeText = false,
  geenOptiesTekst = "Geen categorieën gevonden.",
}: ComboSelectFieldProps) {
  const fieldId = id || `combo-select-${label.replace(/\s/g, "-")}`;
  const [open, setOpen] = useState(false);
  // null = niet aan het typen: toon het label van de huidige waarde.
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(() => {
    for (const g of optionGroups) {
      const hit = g.options.find((o) => o.value === value);
      if (hit) return hit.label;
    }
    return allowFreeText ? value : "";
  }, [optionGroups, value, allowFreeText]);

  const filteredGroups = useMemo(() => {
    const q = (query ?? "").trim().toLowerCase();
    if (!q) return optionGroups;
    return optionGroups
      .map((g) => ({
        label: g.label,
        // Matcht de groepsnaam ("rapport") → hele groep tonen.
        options: g.label.toLowerCase().includes(q)
          ? g.options
          : g.options.filter(
              (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q),
            ),
      }))
      .filter((g) => g.options.length > 0);
  }, [optionGroups, query]);

  const flatOptions = useMemo<FlatOption[]>(
    () =>
      filteredGroups.flatMap((g) =>
        g.options.map((o) => ({ ...o, group: g.label })),
      ),
    [filteredGroups],
  );

  const close = useCallback(
    (refocus = false) => {
      setOpen(false);
      setPosition(null);
      setActiveIndex(-1);
      setQuery(null);
      if (refocus) inputRef.current?.focus();
    },
    [setOpen],
  );

  // Panel onder het veld, veldbreedte; flipt naar de kant met de meeste
  // ruimte en klemt de hoogte op de beschikbare viewportruimte zodat de
  // lijst nooit buiten beeld doorloopt.
  useLayoutEffect(() => {
    if (!open) return;
    const input = inputRef.current;
    const panel = panelRef.current;
    if (!input || !panel) return;
    const rect = input.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const spaceBelow = window.innerHeight - 8 - (rect.bottom + PANEL_GAP);
    const spaceAbove = rect.top - PANEL_GAP - 8;
    const placeBelow =
      panelRect.height <= spaceBelow || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(160, placeBelow ? spaceBelow : spaceAbove);
    const height = Math.min(panelRect.height, maxHeight);
    const top = placeBelow
      ? rect.bottom + PANEL_GAP
      : rect.top - PANEL_GAP - height;
    let left = rect.left;
    const width = Math.max(rect.width, 240);
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - 8 - width);
    }
    setPosition({ top, left, width, maxHeight });
  }, [open, flatOptions.length]);

  // Sluiten bij klik buiten veld én panel, en bij scroll/resize buiten het
  // panel (fixed-position raakt anders los van het veld).
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        inputRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      )
        return;
      close();
    };
    const handleScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      close();
    };
    const handleResize = () => close();
    document.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, close]);

  // Actieve optie in beeld houden bij pijltjesnavigatie.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    panelRef.current
      ?.querySelector(`[data-combo-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const selectOption = (opt: FlatOption) => {
    onChange(opt.value);
    close(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(0);
        return;
      }
      if (flatOptions.length === 0) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((prev) => {
        if (prev < 0) return delta === 1 ? 0 : flatOptions.length - 1;
        return (prev + delta + flatOptions.length) % flatOptions.length;
      });
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && flatOptions[activeIndex]) {
        e.preventDefault();
        selectOption(flatOptions[activeIndex]);
      } else if (open) {
        // Vrije tekst: Enter bevestigt wat er staat en sluit het panel.
        if (allowFreeText) {
          e.preventDefault();
          close(true);
        }
      } else {
        e.preventDefault();
        setOpen(true);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        close(true);
      }
    } else if (e.key === "Tab") {
      close();
    }
  };

  const displayValue = query !== null ? query : selectedLabel;
  const showLabel = !compact || (label && label.length > 0);

  let flatIndex = -1;
  return (
    <div className="modal-field" style={wrapperStyle}>
      {showLabel && (
        <label htmlFor={fieldId} style={labelStyle}>
          {label}
          {required && " *"}
        </label>
      )}
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          id={fieldId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${fieldId}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            ...inputStyle,
            ...(compact
              ? { padding: "0.4rem 1.7rem 0.4rem 0.6rem", fontSize: "0.8rem" }
              : {}),
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            const text = e.target.value;
            setQuery(text);
            setActiveIndex(text.trim() ? 0 : -1);
            if (!open) setOpen(true);
            if (allowFreeText) onChange(text);
          }}
          onKeyDown={handleKeyDown}
        />
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            pointerEvents: "none",
            color: "#6b7280",
            transition: "transform 0.12s",
          }}
        >
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            stroke="currentColor"
            strokeWidth={1.5}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={`${fieldId}-listbox`}
            role="listbox"
            style={{
              position: "fixed",
              zIndex: 4000,
              ...(position ?? {
                top: -9999,
                left: -9999,
                width: 240,
                // Eerste meting: ruim genoeg om de volledige lijst te meten;
                // daarna klemt de positioneringseffect op de viewport.
                maxHeight: "min(85vh, 900px)",
              }),
              visibility: position ? "visible" : "hidden",
              overflowY: "auto",
              background: "var(--color-surface, #fff)",
              border: "1px solid var(--color-border, #d1d5db)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
              padding: "4px 0",
            }}
          >
            {flatOptions.length === 0 ? (
              <div
                style={{
                  padding: "8px 10px",
                  fontSize: "0.75rem",
                  color: "#6b7280",
                }}
              >
                {allowFreeText
                  ? "Geen suggesties — het getypte adres wordt gebruikt."
                  : geenOptiesTekst}
              </div>
            ) : (
              filteredGroups.map((g, gi) => (
                <div key={g.label}>
                  {g.label && (
                    <div
                      style={{
                        padding: gi === 0 ? "3px 10px 1px" : "6px 10px 1px",
                        marginTop: gi === 0 ? 0 : 3,
                        borderTop:
                          gi === 0
                            ? "none"
                            : "1px solid var(--btn-light-bg, #e1e1d2)",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                        color: "#6b7280",
                      }}
                    >
                      {g.label}
                    </div>
                  )}
                  {g.options.map((opt) => {
                    flatIndex += 1;
                    const idx = flatIndex;
                    const isSelected = opt.value === value;
                    const isActive = idx === activeIndex;
                    return (
                      <div
                        key={opt.value}
                        role="option"
                        aria-selected={isSelected}
                        data-combo-index={idx}
                        // mousedown i.p.v. click: anders blurt het veld eerst
                        // en sluit het panel vóór de selectie landt.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectOption({ ...opt, group: g.label });
                        }}
                        onMouseEnter={() => setActiveIndex(idx)}
                        style={{
                          padding: "3px 10px 3px 22px",
                          fontSize: "0.75rem",
                          lineHeight: 1.5,
                          cursor: "pointer",
                          position: "relative",
                          background: isActive
                            ? "var(--btn-light-bg, #e1e1d2)"
                            : "transparent",
                          fontWeight: isSelected ? 600 : 400,
                          color: "var(--color-text, #111827)",
                        }}
                      >
                        {isSelected && (
                          <span
                            aria-hidden
                            style={{
                              position: "absolute",
                              left: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "0.7rem",
                            }}
                          >
                            ✓
                          </span>
                        )}
                        {opt.label}
                        {opt.toelichting && (
                          <span
                            data-toelichting
                            style={{
                              marginLeft: 6,
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                              fontWeight: 400,
                              color: "var(--btn-dark-bg)",
                            }}
                          >
                            {opt.toelichting}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
